import {
  datamuseQuery,
  isName,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

/**
 * The Word Pattern Finder's compiler: what the reader typed → Datamuse queries and one
 * exact local test.
 *
 * Datamuse's `sp` ("spelled like") parameter takes a wildcard pattern — `?` is one
 * character, `*` any run of characters, including none — and returns at most 1,000
 * matches. Three things make the compiler more than a pass-through:
 *
 * - A pattern with no wildcard is read as "spelled similarly", which returns near misses
 *   (`sp=cat` would include `cut`), and the filters cannot always be folded into one
 *   pattern. So every result must also pass an exact local test.
 * - A broad pattern such as `*q*` hits the 1,000 cap, and the cut is not alphabetical or
 *   by frequency alone — common words can fall outside it. When the reader gives a
 *   length and a "contains" string, the compiler therefore asks one fully specified
 *   question per position instead (`q????`, `?q???`, … for five-letter words with q),
 *   which together are complete.
 * - Datamuse's vocabulary includes acronyms written in lower case (`crt`, `cbt`) and
 *   surnames; `isAbbreviationOrName` lets the widget hide them.
 */

export const MAX_MATCHES = 1000;
export const MIN_LENGTH = 2;
export const MAX_LENGTH = 15;

export interface PatternSpec {
  /** Letters with `?` and `*`. */
  pattern: string;
  startsWith: string;
  endsWith: string;
  contains: string;
  /** Exact length in letters, or null for any. */
  length: number | null;
}

export interface CompiledPattern {
  /** The `sp` values sent to Datamuse — one, or one per position of "contains". */
  patterns: string[];
  /** Every constraint, checked exactly against each result. */
  test: (word: string) => boolean;
}

export type CompileResult =
  | { ok: true; compiled: CompiledPattern }
  | { ok: false; reason: "empty" | "invalid" | "impossible" };

const RE_PATTERN = /^[\p{L}?*]*$/u;
const RE_LETTERS = /^\p{L}*$/u;
const RE_WILDCARD_RUN = /\*+/g;
const RE_SPACES = /\s+/g;
const RE_VOWEL = /[aeiouy]/;

function clean(value: string): string {
  return value.toLowerCase().replace(RE_SPACES, "");
}

function letters(word: string): string[] {
  return Array.from(word);
}

/** `t??k` → /^t\p{L}\p{L}k$/u. Input is pre-validated: letters, `?` and `*` only. */
export function patternRegex(pattern: string): RegExp {
  let source = "";
  for (const char of pattern) {
    if (char === "?") {
      source += "\\p{L}";
    } else if (char === "*") {
      source += "\\p{L}*";
    } else {
      source += char;
    }
  }
  return new RegExp(`^${source}$`, "u");
}

/**
 * Lay fixed letters onto a row of `?` slots. Returns false if a slot already holds a
 * different letter — "starts with ab" and "ends with c" cannot share a two-letter word.
 */
function place(slots: string[], part: readonly string[], at: number): boolean {
  for (const [offset, char] of part.entries()) {
    const current = slots[at + offset];
    if (current !== "?" && current !== char) {
      return false;
    }
    slots[at + offset] = char;
  }
  return true;
}

/** Every fully specified pattern of `length` letters that meets all three filters. */
export function positionPatterns(
  length: number,
  startsWith: string,
  endsWith: string,
  contains: string
): string[] {
  const start = letters(startsWith);
  const end = letters(endsWith);
  const inner = letters(contains);
  if (start.length > length || end.length > length || inner.length > length) {
    return [];
  }
  const found = new Set<string>();
  for (let at = 0; at + inner.length <= length; at += 1) {
    const slots: string[] = new Array(length).fill("?");
    if (
      place(slots, start, 0) &&
      place(slots, end, length - end.length) &&
      place(slots, inner, at)
    ) {
      found.add(slots.join(""));
    }
  }
  return [...found];
}

/** The narrowest patterns the filters allow, when no pattern was typed. */
function filterPatterns(spec: Omit<PatternSpec, "pattern">): string[] {
  const { startsWith, endsWith, contains, length } = spec;
  if (length !== null) {
    return positionPatterns(length, startsWith, endsWith, contains);
  }
  if (startsWith || endsWith) {
    return [`${startsWith}*${endsWith}`];
  }
  return contains ? [`*${contains}*`] : [];
}

function normalize(raw: PatternSpec): PatternSpec {
  return {
    pattern: clean(raw.pattern).replace(RE_WILDCARD_RUN, "*"),
    startsWith: clean(raw.startsWith),
    endsWith: clean(raw.endsWith),
    contains: clean(raw.contains),
    length: raw.length,
  };
}

function isValid(spec: PatternSpec): boolean {
  return (
    RE_PATTERN.test(spec.pattern) &&
    [spec.startsWith, spec.endsWith, spec.contains].every((part) =>
      RE_LETTERS.test(part)
    )
  );
}

function isBlank(spec: PatternSpec): boolean {
  const pattern = spec.pattern === "*" ? "" : spec.pattern;
  return !(
    pattern ||
    spec.startsWith ||
    spec.endsWith ||
    spec.contains ||
    spec.length !== null
  );
}

/** Compile the form into queries and an exact test, or say why not. */
export function compilePattern(raw: PatternSpec): CompileResult {
  const spec = normalize(raw);
  if (!isValid(spec)) {
    return { ok: false, reason: "invalid" };
  }
  if (isBlank(spec)) {
    return { ok: false, reason: "empty" };
  }
  const patterns =
    spec.pattern && spec.pattern !== "*"
      ? [spec.pattern]
      : filterPatterns(spec);
  if (patterns.length === 0) {
    return { ok: false, reason: "impossible" };
  }
  const shapes = patterns.map(patternRegex);
  const test = (word: string) =>
    shapes.some((shape) => shape.test(word)) &&
    word.startsWith(spec.startsWith) &&
    word.endsWith(spec.endsWith) &&
    word.includes(spec.contains) &&
    (spec.length === null || letters(word).length === spec.length);
  return { ok: true, compiled: { patterns, test } };
}

export function patternQuery(sp: string): string {
  return datamuseQuery({ sp, md: "pf", max: MAX_MATCHES });
}

/**
 * Lower-case acronyms (`crt`, `cwt`) and surnames are in Datamuse's vocabulary but not
 * in a crossword. No vowel or y is the acronym test, which also drops a handful of
 * genuine words such as `nth` and `cwm`.
 */
export function isAbbreviationOrName(result: WordResult): boolean {
  return !RE_VOWEL.test(result.word.toLowerCase()) || isName(result);
}

export type PatternSort = "common" | "alphabetical" | "length";

export interface PatternMatch {
  word: string;
  frequency: number;
}

function sortMatches(
  matches: PatternMatch[],
  sort: PatternSort
): PatternMatch[] {
  const alphabetical = (a: PatternMatch, b: PatternMatch) =>
    a.word.localeCompare(b.word, "en");
  if (sort === "alphabetical") {
    return matches.sort(alphabetical);
  }
  if (sort === "length") {
    return matches.sort(
      (a, b) =>
        letters(a.word).length - letters(b.word).length || alphabetical(a, b)
    );
  }
  return matches.sort(
    (a, b) => b.frequency - a.frequency || alphabetical(a, b)
  );
}

/** Every answer's results that pass the exact test, merged, de-duplicated and sorted. */
export function matchPattern(
  answers: readonly (readonly WordResult[])[],
  test: (word: string) => boolean,
  { sort, abbreviations }: { sort: PatternSort; abbreviations: boolean }
): PatternMatch[] {
  const seen = new Set<string>();
  const matches: PatternMatch[] = [];
  for (const result of answers.flat()) {
    const word = result.word.toLowerCase();
    if (seen.has(word) || !test(word)) {
      continue;
    }
    seen.add(word);
    if (abbreviations || !isAbbreviationOrName(result)) {
      matches.push({ word, frequency: result.frequency ?? 0 });
    }
  }
  return sortMatches(matches, sort);
}
